import type { Option } from '@resi-media/resi-ui';
import { atom } from 'jotai';
import type { CachedFetchLoadInFlight, StoreData } from '@studio/helpers/tsUtils';
import { Venues } from '@studio/types';
import type { Customer, PrefixCallback } from '@studio/types';

export const customerAtom = atom<StoreData<Customer.Get.Customer | null>>({
  data: null,
  error: undefined,
  hasFetched: false,
  timestamp: 0,
});

export const customerLoadInFlightAtom = atom<CachedFetchLoadInFlight | null>(null);

export const customerNamesAtom = atom<StoreData<Customer.Get.CustomerNames[] | null>>({
  data: null,
  error: undefined,
  hasFetched: false,
  timestamp: 0,
});

export const customerNamesLoadInFlightAtom = atom<CachedFetchLoadInFlight | null>(null);

export const customerFtsAtom = atom<StoreData<Customer.Get.FirstTimeSetupStatus | null>>({
  data: null,
  error: undefined,
  hasFetched: false,
  timestamp: 0,
});

export const customerFtsLoadInFlightAtom = atom<CachedFetchLoadInFlight | null>(null);

export const isCustomerPollingAtom = atom(false);

export const customerNameOptionsAtom = atom<Option[]>((get) => {
  const { data } = get(customerNamesAtom);
  if (!data) {
    return [];
  }
  return data.map(({ name, uuid }) => ({
    label: name,
    value: uuid,
  }));
});

export const customerUsageInfoAtom = atom<Customer.Components.Usage | null>((get) => {
  const { data: customer } = get(customerAtom);
  if (!customer) return null;
  const { usageSummary } = customer;
  const broadcast = Math.max(0, usageSummary.nonProductionSiteLicenses - usageSummary.inUseNonProductionSiteLicenses);
  const ent = Math.max(0, usageSummary.enterpriseLicenses - usageSummary.inUseEnterpriseLicenses);
  const cloud = Math.max(0, usageSummary.lanCloudSiteLicenses - usageSummary.inUseLanCloudSiteLicenses);
  const pro = Math.max(0, usageSummary.professionalLicenses - usageSummary.inUseProfessionalLicenses);

  return {
    [Venues.LICENSE_TYPES.BROADCAST]: broadcast,
    [Venues.LICENSE_TYPES.CLOUD_ENT]: ent,
    [Venues.LICENSE_TYPES.LAN_CLOUD]: cloud,
    [Venues.LICENSE_TYPES.CLOUD_PRO]: pro,
    availableLicenses: broadcast + ent + cloud + pro,
  };
});

export const buildDeleteAfterOptions = (customer: Customer.Get.Customer | null, prefixT: PrefixCallback): Option[] => {
  const deleteAfterArray = customer?.webMaxDeleteAfter
    ? Array.from(Array(customer.webMaxDeleteAfter + 1).keys()).slice(1)
    : [];

  return deleteAfterArray.map((n) => ({
    value: n,
    label: prefixT('fields.deleteAfter.label', { count: n }),
  }));
};

export const buildEncoderChannelDeleteAfterOptions = (
  customer: Customer.Get.Customer | null,
  prefixT: PrefixCallback
): Option[] => {
  const maxDeleteAfter = customer?.multisitePlan ? 30 : (customer?.webMaxDeleteAfter ?? 0);
  const deleteAfterArray = Array.from({ length: maxDeleteAfter }, (_, i) => i + 1);
  return deleteAfterArray.map((n) => ({
    value: n,
    label: prefixT('fields.deleteAfter.label', { count: n }),
  }));
};

export const invalidateCustomerCacheAtom = atom(null, (_get, set) => {
  set(customerAtom, (prev) => ({ ...prev, hasFetched: false, timestamp: 0 }));
});

export const invalidateCustomerFtsCacheAtom = atom(null, (_get, set) => {
  set(customerFtsAtom, (prev) => ({ ...prev, hasFetched: false, timestamp: 0 }));
});

export const resetCustomerAtom = atom(null, (_get, set) => {
  set(customerAtom, { data: null, error: undefined, hasFetched: false, timestamp: 0 });
  set(customerNamesAtom, { data: null, error: undefined, hasFetched: false, timestamp: 0 });
  set(customerFtsAtom, { data: null, error: undefined, hasFetched: false, timestamp: 0 });
  set(isCustomerPollingAtom, false);
});
